import { Component, DestroyRef, inject, OnChanges, OnInit, signal, SimpleChanges, WritableSignal, ChangeDetectionStrategy, input, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BreadCrumbService } from '../../../../@vex/components/breadcrumbs/BreadCrumbService';
import { SessionService } from '../../../services/session.service';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Contact } from '@eadsysteme/contactclient-v1';
import dayjs from 'dayjs';
import { Document, DocumentControllerService } from '@eadsysteme/objectstoreclient-v1';
import { SnackbarService } from '../../../services/snackbar.service';
import { fadeInUp400ms } from '../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../@vex/animations/stagger.animation';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrderedNameDetail, RealEstateContact, RealEstateService, ServiceCompany, ServiceCompanyService, ViewQuery } from '@eadsysteme/ewsrealestateclient-v2';
import { SettingControllerService } from '@eadsysteme/settingclient-v1';
import { MailRelayControllerService, SimpleMailMessage } from '@eadsysteme/mailrelayclient-v1';
import { Icons } from '../../../util/Icons';
import { NumberUtil } from '../../../util/NumberUtil';
import { TenantUnitService } from '@eadsysteme/ewsrealestateclient-v1';
import ContentTypeEnum = Document.ContentTypeEnum;

@Component({
	selector: 'vex-file-upload',
	imports: [
		MatCardModule,
		MatButtonModule,
		MatInputModule,
		MatFormFieldModule,
		MatIconModule,
		FormsModule,
		ReactiveFormsModule,
		MatSelect,
		MatOption,
		TranslocoDirective,
		MatRadioGroup,
		MatRadioButton,
		MatProgressSpinnerModule,
	],
	templateUrl: './file-upload.component.html',
	styleUrl: './file-upload.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class FileUploadComponent implements OnInit, OnChanges {
	breadCrumbService = inject(BreadCrumbService);
	protected sessionService = inject(SessionService);
	private documentService = inject(DocumentControllerService);
	private fb = inject(FormBuilder);
	protected icons = inject(Icons);
	private serviceCompanyService = inject(ServiceCompanyService);
	private realEstateService = inject(RealEstateService);
	private tenantUnitService = inject(TenantUnitService);
	private transloco = inject(TranslocoService);
	private settingService = inject(SettingControllerService);
	private mailService = inject(MailRelayControllerService);
	private snackBar = inject(SnackbarService);
	private numberUtil = inject(NumberUtil);

	private destroyRef = inject(DestroyRef);
	uploadForm: FormGroup;
	selectedFile: File | null = null;
	isDragOver = false;
	fileTypeOptions = ['PDF', 'DOC', 'DOCX', 'TXT', 'CSV', 'ODT'].sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
	filePickerAcceptFilter = this.fileTypeOptions
		.map((ft) => {
			return '.' + ft.toLowerCase();
		})
		.join(',');
	docTypeOptions = ['A', 'D', 'W', 'E', 'HKA-G', 'HKA-E', 'UVI-G', 'UVI-E', 'OTHER'];
	showFileUpload = signal(false);
	isLoading = signal(false);

	readonly MAX_UPLOAD_SIZE_BYTES = 200 * 1024 * 1024; // 200MB

	readonly fileUploaded = output<Document>();

	propertyManagementContacts = signal<Contact[]>([]);

	readonly preselectedUploadType = input<string>('transfer');

	protected serviceCompanies: Set<ServiceCompany> = new Set<ServiceCompany>();
	private currentRealEstates: Array<RealEstateContact>;
	protected currentRealEstateNumbers: number[] = [];
	protected currentRealEstateNumbersAndIds: Map<number, string> = new Map<number, string>();
	protected tenantUnitNumbers: WritableSignal<string[]> = signal([]);

	mailReceiver: string;

	constructor() {
		this.propertyManagementContacts.set(this.sessionService.propertyManagementContacts());
		this.sessionService.currentAreas?.forEach((area) => {
			this.serviceCompanyService
				.getServiceCompany(area)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (serviceCompany) => {
						this.serviceCompanies.add(serviceCompany);
						if (this.serviceCompanies.size === 1) {
							this.uploadForm.get('targetSc')?.disable();
						} else {
							this.uploadForm.get('targetSc')?.enable();
						}
					},
				});
		});

		this.loadServiceCompanySetting(this.sessionService.effectiveArea);

		this.createUploadForm();
		if (this.sessionService.currentAreas?.length > 1) {
			this.uploadForm.addControl('targetSc', new FormControl('', Validators.required));
		}
		this.uploadForm.disable();

		const viewQuery: ViewQuery = {};
		this.currentRealEstateNumbers = [];
		this.currentRealEstateNumbersAndIds = new Map<number, string>();
		this.realEstateService
			.search(this.sessionService.effectiveArea, viewQuery)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (currentRealEstates: any) => {
					this.currentRealEstates = currentRealEstates.items as Array<RealEstateContact>;
					this.currentRealEstates.sort((a, b) => a.realEstateNumber - b.realEstateNumber);
					this.currentRealEstates?.forEach((value) => {
						if (this.currentRealEstateNumbers.includes(value.realEstateNumber)) {
							return;
						}
						this.currentRealEstateNumbers.push(value.realEstateNumber);
						this.currentRealEstateNumbersAndIds.set(value.realEstateNumber, value.realEstateId);
					});

					this.preSelectRealEstate();
				},
			});
	}

	ngOnInit(): void {
		this.propertyManagementContacts().sort((a, b) => this.getPropertyManagementName(a).localeCompare(this.getPropertyManagementName(b)));
		this.preSelectPropertyManagement();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['preselectedUploadType'] && this.uploadForm) {
			this.uploadForm.patchValue({ uploadType: this.preselectedUploadType() });
		}
	}

	private preSelectRealEstate() {
		// No-op: pin feature removed
	}

	private preSelectPropertyManagement() {
		if (this.sessionService.selectedPropertyManagementId && this.propertyManagementContacts?.length) {
			const selectedPm = this.propertyManagementContacts().find((c) => c.id === this.sessionService.selectedPropertyManagementId);
			if (selectedPm) {
				this.uploadForm.get('propertyManagementContact')?.setValue(selectedPm);
			}
		}
	}

	loadTenantUnitNumbers(realEstateNumber: number) {
		this.tenantUnitNumbers.set([]);
		const tuNumberControl = this.uploadForm.get('tuNumber');
		tuNumberControl?.disable();
		tuNumberControl?.setValue('');
		tuNumberControl?.markAsUntouched();
		if (!realEstateNumber) {
			return;
		}
		this.tenantUnitService
			.getByRealEstateId(this.sessionService.effectiveArea, this.currentRealEstateNumbersAndIds.get(realEstateNumber))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (tenantUnits) => {
					this.tenantUnitNumbers.set(tenantUnits.map((value) => value.number + '/' + value.subNumber));
					if (!this.uploadForm.get('reNumber').disabled) {
						tuNumberControl?.enable({});
					}
				},
				error: () => {
					this.snackBar.error(this.transloco.translate('documentTransfer.errorLoadingTenantUnits', { realEstateNumber: realEstateNumber }));
				},
			});
	}

	createUploadForm() {
		this.uploadForm = this.fb.group({
			uploadType: [this.preselectedUploadType() || 'transfer', Validators.required],
			docType: ['', Validators.required],
			fileName: ['', [Validators.required, Validators.maxLength(255), Validators.pattern(/^[0-9a-zA-Z_\-. ]+$/)]],
			fileType: ['', Validators.required],
			propertyManagementContact: ['', Validators.required],
			reNumber: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
			tuNumber: ['', [Validators.required, Validators.pattern(/^\d+\/\d+$/)]],
		});

		this.preSelectRealEstate();
		this.preSelectPropertyManagement();
	}

	loadServiceCompanySetting(area: number) {
		if (this.mailReceiver && area === this.sessionService.effectiveArea) {
			return; //still using same serviceCompany, no need to reload settings;
		}
		this.settingService
			.getConfigValue(area, 'portal', 'documentTransfer', 'notificationMail')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (serviceCompanySetting) => {
					this.mailReceiver = serviceCompanySetting.value;
				},
			});
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];
			this.processFile(file);
		}
	}

	onFileDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver = false;

		const files = event.dataTransfer?.files;
		if (files && files.length > 0) {
			this.processFile(files[0]);
		}
	}

	processFile(file: File) {
		this.createUploadForm();
		this.uploadForm.disable();
		if (file.size > this.MAX_UPLOAD_SIZE_BYTES) {
			this.snackBar.error(
				this.transloco.translate('documentTransfer.errorFileTooBig', { maxFileSize: this.getByteFileSizeString(this.MAX_UPLOAD_SIZE_BYTES) })
			);
			return;
		}

		this.selectedFile = file;
		const fileNameParts = file.name.split('.');
		this.uploadForm.get('fileName')?.setValue(file.name);

		if (fileNameParts.length > 1) {
			const fileEnding = fileNameParts[fileNameParts.length - 1].toUpperCase();
			if (this.fileTypeOptions.indexOf(fileEnding) === -1) {
				this.snackBar.warn(this.transloco.translate('documentTransfer.unsupportedFileType'));
				return;
			}
			this.uploadForm.enable();
			this.uploadForm.get('fileName')?.disable();
			this.uploadForm.get('fileType')?.setValue(fileEnding);
			this.uploadForm.get('fileType')?.disable();
			this.uploadForm.get('tuNumber')?.disable();
		}
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver = true;
	}

	onDragLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver = false;
	}

	async uploadFile() {
		if (!this.selectedFile) {
			this.snackBar.error(this.transloco.translate('documentTransfer.errorNoFileSelected'));
			return;
		}

		if (!this.uploadForm.valid) {
			this.snackBar.error(this.transloco.translate('common.formInvalid'));
			return;
		}

		try {
			const senderPm: Contact = this.uploadForm.get('propertyManagementContact')?.value;
			const document: Document = {
				origin: 'customerPortal',
				module: 'DocumentTransfer',
				categoryName: senderPm.accountNumber,
				name: this.uploadForm.get('fileName')?.value,
				size: this.selectedFile.size,
				contentType: this.uploadForm.get('fileType')?.value,
				properties: [
					{
						name: 'isArchived',
						value: (this.uploadForm.get('uploadType')?.value === 'archive').toString(),
					},
					{
						name: 'uploadDate',
						value: dayjs().toString(),
					},
					{
						name: 'automaticDeletionInDays',
						value: this.uploadForm.get('uploadType')?.value === 'transfer' ? '90' : null,
					},
					{
						name: 'docType',
						value: this.uploadForm.get('docType')?.value,
					},
					{
						name: 'reNumber',
						value: this.uploadForm.get('reNumber')?.value,
					},
					{
						name: 'tuNumber',
						value: this.uploadForm.get('tuNumber')?.value,
					},
				],
			};
			const file = this.selectedFile;

			this.isLoading.set(true);
			this.documentService
				.createOrUpdateDocument(senderPm.area, JSON.stringify(document), file)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (response) => {
						this.snackBar.success(
							this.transloco.translate('uploadDirective.uploadSuccessful', { fileName: this.getFileNameWithExtension(response) })
						);
						this.fileUploaded.emit(response);
						this.selectedFile = null;
						this.createUploadForm();
						this.uploadForm.disable();
						this.isLoading.set(false);
						this.sendMail(senderPm);
					},
					error: () => {
						this.snackBar.error(this.transloco.translate('uploadDirective.uploadFailed'));
						this.isLoading.set(false);
					},
				});
		} catch {
			this.snackBar.error(this.transloco.translate('uploadDirective.uploadFailed'));
			this.isLoading.set(false);
		}
	}

	getFileNameWithExtension(document: Document): string {
		if (!document) {
			return '';
		}

		let fileName = document.name;
		if (document.contentType !== ContentTypeEnum.UNDEFINED && !fileName.toLowerCase().endsWith(document.contentType.toLowerCase())) {
			fileName += '.' + document.contentType.toLowerCase();
		}
		return fileName;
	}

	getByteFileSizeString(fileSize: number): string {
		let size = fileSize ?? 0;
		if (size < 1024) {
			return size + ' B';
		}
		size /= 1024;
		if (size < 1024) {
			return this.numberUtil.scaleAndReplaceDot(size, 1) + ' KB';
		}
		size /= 1024;
		return this.numberUtil.scaleAndReplaceDot(size, 1) + ' MB';
	}

	getPropertyManagementName(propertyManagement: Contact): string {
		if (!propertyManagement) {
			return '';
		}
		return [propertyManagement.firstName, propertyManagement.name].join(' ');
	}

	prettifyServiceCompanyName(companyNames: Array<OrderedNameDetail>) {
		return companyNames.map((value) => value.name).join(', ');
	}

	private sendMail(senderPm: Contact) {
		if (!this.mailReceiver) {
			this.snackBar.error(this.transloco.translate('uploadDirective.mailFailed'));
			return;
		}
		const simpleMailMessage: SimpleMailMessage = {
			from: 'Portal Verbrauchsinformation und Heizkostenabrechnung <noreply@ara-portal.de>',
			to: this.mailReceiver,
			subject: 'Neues Dokument im Dokumententausch',
			body:
				'Im Dokumententausch des Arasys-Portals wurde ein neues Dokument von der Firma ' +
				this.getPropertyManagementName(senderPm) +
				' für Sie freigegeben',
		};
		this.mailService
			.sendSimpleMail(simpleMailMessage)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackBar.success(this.transloco.translate('uploadDirective.mailSent'));
				},
				error: () => {
					this.snackBar.error(this.transloco.translate('uploadDirective.mailFailed'));
				},
			});
	}
}
